var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * CZNA-1518 — the findings of a check, the way the form's banners list them: grouped by the
 * tab the field sits on, each row led by the field's name.
 *
 * `ValidationIssueList` leads with the rule's message and then says where the field is,
 * which on the form says one thing twice ("Chybí povinné pole 670" over "Pole 670 Citovaný
 * zdroj na záložce Citované zdroje") and closes each row with the rule's code. Here the
 * group heading is the tab, so the tab badge and the list count the same thing; the row
 * names the field, the rule's message (verbatim — it is the Aleph text) explains it, and
 * the whole row is the way to the field. The code stays out of sight: it names the rule
 * for whoever maintains it and tells the cataloguer nothing. It is kept as the row's
 * tooltip on the rule's text and as `data-test-validation-issue-code`.
 *
 * Outside the form — the detail panel, RecordEdit's modal — `ValidationIssueList` stays:
 * there is no tab to group by and no field to jump to.
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
var SEVERITY_COLOR = {
    BLOCKING: '#b4261f',
    WARNING: '#8a5d00',
};
var groupByTab = function (issues, describeLocation) {
    var groups = new Map();
    issues.forEach(function (issue) {
        var _a, _b, _c, _d;
        var described = ((_a = issue.location) === null || _a === void 0 ? void 0 : _a.tag) ? describeLocation(issue.location) : null;
        var group;
        if (described === null || described === void 0 ? void 0 : described.tabLabel) {
            group = { key: "tab:".concat(described.tabLabel), tabLabel: described.tabLabel, order: (_b = described.tabIndex) !== null && _b !== void 0 ? _b : 0 };
        }
        else if ((_c = issue.location) === null || _c === void 0 ? void 0 : _c.tag) {
            group = { key: 'notOnForm', fallback: 'notOnForm', order: Number.MAX_SAFE_INTEGER };
        }
        else {
            group = { key: 'wholeRecord', fallback: 'wholeRecord', order: Number.MAX_SAFE_INTEGER - 1 };
        }
        var existing = (_d = groups.get(group.key)) !== null && _d !== void 0 ? _d : __assign(__assign({}, group), { rows: [] });
        existing.rows.push({ issue: issue, described: described });
        groups.set(group.key, existing);
    });
    return Array.from(groups.values()).sort(function (a, b) { return a.order - b.order; });
};
var SeverityMark = function (_a) {
    var severity = _a.severity;
    return (severity === 'WARNING' ? (
    // A triangle, not just another colour — the two must not differ by hue alone.
    React.createElement("span", { "aria-hidden": "true", style: {
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: "10px solid ".concat(SEVERITY_COLOR.WARNING),
            flex: 'none',
        } })) : (React.createElement("span", { "aria-hidden": "true", style: {
            width: '9px',
            height: '9px',
            borderRadius: '50%',
            background: SEVERITY_COLOR.BLOCKING,
            flex: 'none',
            margin: '0 1.5px',
        } })));
};
var IssueRow = function (_a) {
    var _b;
    var issue = _a.issue, described = _a.described, onLocationClick = _a.onLocationClick;
    var tag = (_b = issue.location) === null || _b === void 0 ? void 0 : _b.tag;
    // The field is named only where the form names it; a bare tag still beats nothing.
    var title = null;
    if (tag) {
        title = (described === null || described === void 0 ? void 0 : described.fieldLabel)
            ? "".concat(described.fieldLabel, " \u00B7 ").concat(tag)
            : React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.issue.field", values: { tag: tag } });
    }
    // With a form to describe the location, jump only to a field it has.
    var canJump = !!onLocationClick && !!described;
    var content = (React.createElement(React.Fragment, null,
        React.createElement("span", { style: { display: 'flex', width: '14px', justifyContent: 'center', paddingTop: '6px' } },
            React.createElement(SeverityMark, { severity: issue.severity })),
        React.createElement("span", { style: { flex: 1, minWidth: 0 } },
            title && (React.createElement("span", { "data-test-validation-issue-location": true, style: { display: 'block', fontWeight: 600 } }, title)),
            React.createElement("span", { "data-test-validation-issue-message": true, title: issue.code, style: { display: 'block', color: title ? '#555' : 'inherit', fontSize: title ? '0.93em' : undefined } }, issue.message)),
        canJump && (React.createElement("span", { style: { color: '#0073e6', whiteSpace: 'nowrap', alignSelf: 'center' } },
            React.createElement(FormattedMessage, { id: issue.severity === 'WARNING'
                    ? 'ui-czna-authorities.validation.issue.show'
                    : 'ui-czna-authorities.validation.issue.fix' }),
            ' ›'))));
    var rowStyle = {
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        width: '100%',
        padding: '6px 8px',
        borderRadius: '4px',
        textAlign: 'left',
        color: '#1f2329',
        font: 'inherit',
    };
    return (React.createElement("li", { "data-test-validation-issue": true, "data-test-validation-issue-code": issue.code, "data-test-validation-issue-severity": issue.severity, style: { margin: '0 -8px' } }, canJump ? (React.createElement("button", { type: "button", "data-test-validation-issue-jump": true, onClick: function () { return onLocationClick(issue.location); }, className: "czna-validation-issue-row", style: __assign(__assign({}, rowStyle), { background: 'none', border: 'none', cursor: 'pointer' }) }, content)) : (React.createElement("div", { style: rowStyle }, content))));
};
export var ValidationIssueGroups = function (_a) {
    var issues = _a.issues, describeLocation = _a.describeLocation, onLocationClick = _a.onLocationClick;
    var groups = groupByTab(issues, describeLocation);
    // One group needs no heading only when it is not a tab — a lone tab is still worth naming.
    var headed = groups.length > 1 || groups.some(function (g) { return g.tabLabel; });
    return (React.createElement("div", { "data-test-validation-issue-list": true },
        React.createElement("style", null, '.czna-validation-issue-row:hover,.czna-validation-issue-row:focus-visible{background:#eef0f3 !important}'),
        groups.map(function (group) {
            var _a;
            return (React.createElement("div", { key: group.key, "data-test-validation-issue-group": (_a = group.tabLabel) !== null && _a !== void 0 ? _a : group.key },
                headed && (React.createElement("div", { style: {
                        margin: '8px 0 2px',
                        fontSize: '0.78em',
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: '#5d6570',
                    } }, group.fallback ? (React.createElement(FormattedMessage, { id: "ui-czna-authorities.validation.group.".concat(group.fallback) })) : group.tabLabel)),
                React.createElement("ul", { style: { listStyle: 'none', padding: 0, margin: 0 } }, group.rows.map(function (row, idx) { return (React.createElement(IssueRow, { key: "".concat(row.issue.code, "-").concat(idx), issue: row.issue, described: row.described, onLocationClick: onLocationClick })); }))));
        })));
};
export default ValidationIssueGroups;
